export type GamePhase = 'setup' | 'playing' | 'promotion';
export type GameMode = 'local' | 'online';
export type PieceType = 'K' | 'Q' | 'R' | 'B' | 'N' | 'P' | 'H' | 'E' | 'A';
export type Color = 'white' | 'black';
export type PromotionRule = 'limited' | 'full';
export type GameVariation = 'chess' | 'chessx2' | 'chessx8' | 'chessx32' | 'chess960' | 'chess960x2' | 'chess960x8' | 'chess960x32' | 'chess960blind';
export type Position = [number, number];

export interface Piece {
  piece: PieceType;
  color: Color;
}

export interface PlayerPieces {
  white: PieceType | null;
  black: PieceType | null;
}

export interface CastlingRights {
  whiteKingSide: boolean;
  whiteQueenSide: boolean;
  blackKingSide: boolean;
  blackQueenSide: boolean;
}

export interface GameMove {
  from: Position;
  to: Position;
  piece: Piece;
  capturedPiece?: Piece;
  notation: string;
  isCheck?: boolean;
  isCheckmate?: boolean;
  isCastling?: boolean;
  isEnPassant?: boolean;
}

export type GameStatus = 'ongoing' | 'checkmate' | 'stalemate' | 'resignation' | 'draw_agreement' | 'timeout';

export interface GameState {
  status: GameStatus;
  winner?: Color;
}

export type BoardSquare = Piece | null;
export type Board = BoardSquare[][];

export const PIECE_SYMBOLS: Record<Color, Record<PieceType, string>> = {
  white: { K: '♔', Q: '♕', R: '♖', B: '♗', N: '♘', P: '♙', H: '🩐', E: '🩏', A: '🩎' },
  black: { K: '♚', Q: '♛', R: '♜', B: '♝', N: '♞', P: '♟', H: '🩓', E: '🩒', A: '🩑' }
};

export type OnlineProvider = 'github' | 'google' | 'facebook' | 'lichess';

export interface OnlineUser {
  id: string;
  name: string;
  provider: OnlineProvider;
  email?: string;
  avatarUrl?: string;
}

export interface TimeControl {
  minutes: number;
  increment: number;
  incrementType: 'none' | 'fischer' | 'bronstein';
}

export interface OnlineRoomConfig {
  gameVariation: GameVariation;
  playerPieces: PlayerPieces;
  promotionRule: PromotionRule;
  timeControl?: TimeControl;
  chess960PositionId?: number | null;
}

export type OnlineUserSummary = Pick<OnlineUser, 'id' | 'name' | 'provider' | 'avatarUrl'>;

export type OnlineLobbyAvailability = 'available' | 'pending_invite' | 'in_game';
export type OnlineInviteStatus = 'pending' | 'accepted' | 'refused' | 'cancelled';
export type OnlineColorPreference = Color | 'random';
export type OnlineRoomSource = 'private_room' | 'lobby_invite';
export type OnlineRoomVisibility = 'private' | 'public';
export type OnlineRoomRole = Color;

export interface OnlineRoomMetadata {
  source: OnlineRoomSource;
  visibility: OnlineRoomVisibility;
  listedInLobby: boolean;
  inviteId?: string | null;
}

export interface OnlineLobbyPresenceUser extends OnlineUserSummary {
  availability: OnlineLobbyAvailability;
  activeRoomId: string | null;
  pendingInviteId: string | null;
  updatedAt: number;
}

export interface OnlineDirectInvite {
  id: string;
  status: OnlineInviteStatus;
  inviter: OnlineUserSummary;
  invitee: OnlineUserSummary;
  config: OnlineRoomConfig;
  colorPreference: OnlineColorPreference;
  roomId: string | null;
  createdAt: number;
  updatedAt: number;
  respondedAt: number | null;
}

export interface OnlineRoomSession {
  roomId: string;
  role: Color;
  whiteName: string;
  blackName: string | null;
  metadata?: OnlineRoomMetadata;
}

export type OnlineRoomParticipantSession = OnlineRoomSession;

export interface OnlineGameSnapshot {
  version: number;
  gameVariation: GameVariation;
  playerPieces: PlayerPieces;
  promotionRule: PromotionRule;
  gamePhase: GamePhase;
  board: Board;
  currentPlayer: Color;
  gameHistory: GameMove[];
  promotionSquare: Position | null;
  enPassantTarget: Position | null;
  castlingRights: CastlingRights;
  kingPositions: {
    white: Position | null;
    black: Position | null;
  };
  rookPositions: {
    whiteKingSide: Position | null;
    whiteQueenSide: Position | null;
    blackKingSide: Position | null;
    blackQueenSide: Position | null;
  };
  resignedBy: Color | null;
  drawOfferedBy: Color | null;
  drawAgreed: boolean;
  /** Set when a player ran out of time; the winner is the side NOT to move. */
  timeoutWinner?: Color | null;
  whiteTimeMs?: number;
  blackTimeMs?: number;
  lastMoveTimestamp?: number;
}

export interface OnlineLobbyState {
  self: OnlineLobbyPresenceUser | null;
  users: OnlineLobbyPresenceUser[];
  incomingInvites: OnlineDirectInvite[];
  outgoingInvites: OnlineDirectInvite[];
}

export interface OnlineCreateInviteRequest {
  inviteeUserId: string;
  config: OnlineRoomConfig;
  colorPreference: OnlineColorPreference;
}

export interface OnlineInviteResponse {
  invite: OnlineDirectInvite;
}

export interface OnlineAcceptInviteResponse {
  invite: OnlineDirectInvite;
  room: OnlineRoomSession;
  config: OnlineRoomConfig;
  snapshot: OnlineGameSnapshot | null;
}

export interface OnlineLobbyPresenceResponse {
  lobby: OnlineLobbyState;
}

export interface OnlineRoomPresence {
  whiteConnected: boolean;
  blackConnected: boolean;
  whiteName: string | null;
  blackName: string | null;
}

export type OnlineRoomClientMessage =
  | {
    type: 'sync_state';
    snapshot: OnlineGameSnapshot;
  }
  | {
    type: 'request_rematch';
  }
  | {
    type: 'start_game';
  }
  | {
    /** Blind Chess960: submit the player's own secret back rank (8 characters). */
    type: 'submit_blind_setup';
    backRank: string;
  }
  | {
    /** Ask the server to resend the authoritative room state (snapshot or setup status). */
    type: 'request_state';
  }
  | {
    type: 'pong';
  };

export type OnlineRoomServerMessage =
  | {
    type: 'state_update';
    snapshot: OnlineGameSnapshot;
  }
  | {
    type: 'presence';
    presence: OnlineRoomPresence;
  }
  | {
    type: 'error';
    message: string;
  }
  | {
    type: 'room_reset';
    room: OnlineRoomSession;
    config: OnlineRoomConfig;
    requestedByUserId: string;
  }
  | {
    type: 'game_started';
  }
  | {
    /** Blind Chess960: which sides have submitted their secret setup so far. */
    type: 'blind_setup_status';
    whiteSubmitted: boolean;
    blackSubmitted: boolean;
  }
  | {
    type: 'room_closed';
    roomId: string;
    message: string;
    closedByUserId: string;
  }
  | {
    type: 'ping';
  };

export type OnlineLobbyServerMessage =
  | {
    type: 'lobby_state';
    lobby: OnlineLobbyState;
  }
  | {
    type: 'invite_updated';
    invite: OnlineDirectInvite;
  }
  | {
    type: 'presence_updated';
    user: OnlineLobbyPresenceUser;
  };
